import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Card,
  CardContent,
  Divider,
  useTheme,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Backdrop,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import { Edit, Save, Cancel, Refresh, QrCode2 } from '@mui/icons-material';
import { Logger } from '../../../utils/Logger';
import { useSelector } from 'react-redux';
import { RootState } from '../../../state/store';
import { FlexBetween } from '../../../components/FlexBetween';
import { useModulePermissions } from '../../../hooks/useModulePermissions';
import { useWhatsAppData } from '../../../hooks/useWhatsAppData';
import QRCode from 'qrcode';
import { selectLabels } from 'state/dictionary/dictionarySlice';

// QR Code component
const QRCodeDisplay: React.FC<{ qrCodeString: string; isOutdated?: boolean }> = ({ qrCodeString, isOutdated = false }) => {
  const theme = useTheme();
  const labels = useSelector((state: RootState) => selectLabels(state));
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');
  const [qrCodeError, setQrCodeError] = useState<string>('');
  useEffect(() => {
    if (qrCodeString) {
      generateQRCode(qrCodeString);
    } else {
      setQrCodeDataUrl('');
      setQrCodeError('');
    }
  }, [qrCodeString]);

  const generateQRCode = async (qrString: string) => {
    try {
      setQrCodeError('');
      const dataUrl = await QRCode.toDataURL(qrString, {
        width: 300,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      setQrCodeDataUrl(dataUrl);
      Logger.info("src/modules/Settings/WhatsApp/WhatsApp.tsx : generateQRCode()", "QR code generated successfully");
    } catch (error) {
      Logger.error("src/modules/Settings/WhatsApp/WhatsApp.tsx : generateQRCode()", `Failed to generate QR code: ${error}`);
      setQrCodeError('Failed to generate QR code');
      setQrCodeDataUrl('');
    }
  };

  if (!qrCodeString) {
    return (
      <Box
        sx={{
          width: 'min(100%, 300px)',
          height: 'auto',
          aspectRatio: '1 / 1',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '2px dashed #ccc',
          borderRadius: '8px',
          bgcolor: theme.palette.background.default,
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <QrCode2 sx={{ fontSize: 48, color: 'text.secondary' }} />
        <Typography variant="body2" color="textSecondary" textAlign="center">
          {labels?.['modules-settings-whatsapp-no-qr-code-available'] ?? 'No QR Code Available'}
        </Typography>
        <Typography variant="caption" color="textSecondary" textAlign="center">
          {labels?.['modules-settings-whatsapp-start-the-whatsapp-bot-to-generate-qr-code'] ?? 'Start the WhatsApp bot to generate QR code'}
        </Typography>
      </Box>
    );
  }

  if (qrCodeError) {
    return (
      <Box
        sx={{
          width: 'min(100%, 300px)',
          height: 'auto',
          aspectRatio: '1 / 1',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '2px dashed #f44336',
          borderRadius: '8px',
          bgcolor: theme.palette.background.default,
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <QrCode2 sx={{ fontSize: 48, color: 'error.main' }} />
        <Typography variant="body2" color="error.main" textAlign="center">
          {labels?.['modules-settings-whatsapp-qr-code-error'] ?? 'QR Code Error'}
        </Typography>
        <Typography variant="caption" color="textSecondary" textAlign="center">
          {qrCodeError}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: 'min(100%, 300px)',
        height: 'auto',
        aspectRatio: '1 / 1',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: isOutdated ? '2px solid #f44336' : '2px solid #4CAF50',
        borderRadius: '8px',
        bgcolor: '#ffffff',
        overflow: 'hidden',
        position: 'relative',
        opacity: isOutdated ? 0.7 : 1,
      }}
    >
      {qrCodeDataUrl ? (
        <img
          src={qrCodeDataUrl}
          alt="WhatsApp QR Code"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
          }}
        />
      ) : (
        <Box sx={{ textAlign: 'center', p: 2 }}>
          <CircularProgress size={40} color="success" sx={{ mb: 1 }} />
          <Typography variant="body2" color="success.main" fontWeight="bold">
            {labels?.['modules-settings-whatsapp-generating-qr-code'] ?? 'Generating QR Code...'}
          </Typography>
        </Box>
      )}
      
      {/* Status indicator */}
      <Box
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          width: 12,
          height: 12,
          borderRadius: '50%',
          bgcolor: isOutdated ? '#f44336' : '#4CAF50',
          border: '2px solid #ffffff',
        }}
      />
    </Box>
  );
};

export const WhatsApp: React.FC = () => {
  const theme = useTheme();
  const panelTitleSx = {
    color: theme.palette.heading?.main ?? theme.palette.secondary.main,
  };
  const labels = useSelector((state: RootState) => selectLabels(state));
  const { hasInstantUpdateAccess, getInstantUpdateRole, isUserSuperAdmin } = useModulePermissions();
  
  // Use the Redux-based WhatsApp data hook
  const {
    config: configuration,
    loading,
    error,
    deducedStatus,
    isQRCodeOutdated,
    refreshData,
    updateConfig,
  } = useWhatsAppData();

  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'warning' });
  
  // Real-time timestamp for QRCODE status
  const [currentTime, setCurrentTime] = useState<Date>(new Date());
  
  // Countdown timer state
  const [countdown, setCountdown] = useState<number>(0);
  
  // Update current time and countdown every second when status is QRCODE
  useEffect(() => {
    if (deducedStatus === 'QRCODE' && configuration?.qrCodeTimestamp) {
      const interval = setInterval(() => {
        const now = new Date();
        setCurrentTime(now);
        
        // Calculate countdown from last updated timestamp
        const lastUpdated = new Date(configuration.qrCodeTimestamp);
        const targetTime = new Date(lastUpdated.getTime() + 7000); // 7 seconds from last updated
        const remainingTime = Math.max(0, targetTime.getTime() - now.getTime());
        
        setCountdown(Math.ceil(remainingTime / 1000)); // Convert to seconds
      }, 1000); // Update every second
      
      return () => clearInterval(interval);
    }
    
    // If not in QRCODE status or no timestamp, reset countdown
    setCountdown(0);
    return undefined;
  }, [deducedStatus, configuration?.qrCodeTimestamp]);

  const channelName = "WhatsApp";
  const userRole = getInstantUpdateRole();
  const canEdit = false;
  const canView = hasInstantUpdateAccess() || isUserSuperAdmin;

  // Check if user has access to this module
  if (!canView) {
    return (
      <Box pl={{ xs: 1.25, sm: 2.5 }} pt={1} width="100%">
        <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
          {labels?.['modules-settings-whatsapp-access-denied'] ?? 'Access Denied'}
        </Typography>
        <Typography variant="body1" color="textSecondary">
          {labels?.['modules-settings-whatsapp-you-dont-have-permission-to-access-whatsapp-configuration'] ?? 'You don\'t have permission to access WhatsApp configuration.'}
        </Typography>
      </Box>
    );
  }

  const handleOpenDialog = () => {};

  const handleCloseDialog = () => {
    setDialogOpen(false);
    refreshData();
  };

  const handleSaveConfiguration = async (configData: any) => {
    if (!canEdit) return;

    try {
      await updateConfig(configData);
      setSnackbar({ open: true, message: labels?.['modules-settings-whatsapp-whatsapp-configuration-saved-successfully'] ?? 'WhatsApp configuration saved successfully!', severity: 'success' });
      handleCloseDialog();
    } catch (error) {
      Logger.error("src/modules/Settings/WhatsApp/WhatsApp.tsx : handleSaveConfiguration()", `Failed to save configuration: ${error}`);
      setSnackbar({ open: true, message: `Operation failed: ${error instanceof Error ? error.message : String(error)}`, severity: 'error' });
      throw error;
    }
  };

  // Function to get display content based on deduced status
  const getDisplayContent = () => {
    const labels = useSelector((state: RootState) => selectLabels(state));
    const phoneNumber = configuration?.phoneNumber;
    const hasQRCode = configuration?.qrCodeString && configuration.qrCodeString.trim() !== '' && configuration.qrCodeString !== 'undefined';

    // If we're still loading, show loading state
    if (loading && !configuration) {
      return {
        type: 'loading' as const,
        title: labels?.['modules-settings-whatsapp-loading-whatsapp-configuration'] ?? 'Loading WhatsApp Configuration',
        subtitle: labels?.['modules-settings-whatsapp-please-wait'] ?? 'Please wait...',
        message: labels?.['modules-settings-whatsapp-fetching-latest-configuration-from-server'] ?? 'modules-settings-whatsapp-fetching-latest-configuration-from-server',
        color: 'info' as const,
        showQR: false
      };
    }

    if (deducedStatus === 'QRCODE') {
      return {
        type: 'qrcode' as const,
        title: labels?.['modules-settings-whatsapp-scan-qr-code'] ?? 'modules-settings-whatsapp-scan-qr-code',
        subtitle: labels?.['modules-settings-whatsapp-scan-to-connect-whatsapp-bot'] ?? 'modules-settings-whatsapp-scan-to-connect-whatsapp-bot',
        message: labels?.['modules-settings-whatsapp-qr-code-is-ready-for-scanning'] ?? 'modules-settings-whatsapp-qr-code-is-ready-for-scanning',
        color: 'success' as const, // green
        showQR: true
      };
    } else if (deducedStatus === 'OFFLINE') {
      return {
        type: 'offline' as const,
        title: labels?.['modules-settings-whatsapp-offline-title'] ?? 'modules-settings-whatsapp-offline-title',
        subtitle: labels?.['modules-settings-whatsapp-offline-subtitle'] ?? 'modules-settings-whatsapp-offline-subtitle',
        message: phoneNumber ? `Last connected with: ${phoneNumber}` : '',
        color: 'error' as const, // red
        showQR: false
      };
    } else if (deducedStatus === 'ONLINE') {
      return {
        type: 'online' as const,
          title: labels?.['modules-settings-whatsapp-online-title'] ?? 'modules-settings-whatsapp-online-title',
        subtitle: labels?.['modules-settings-whatsapp-online-subtitle'] ?? 'modules-settings-whatsapp-online-subtitle',
        message: labels?.['modules-settings-whatsapp-your-whatsapp-bot-is-online-and-ready-to-send-campaign-messages'] ?? 'modules-settings-whatsapp-your-whatsapp-bot-is-online-and-ready-to-send-campaign-messages',
        color: 'success' as const, // green
        showQR: false
      };
    } else if (deducedStatus === 'BUSY') {
      return {
        type: 'busy' as const,
        title: labels?.['modules-settings-whatsapp-busy-title'] ?? 'modules-settings-whatsapp-busy-title',
        subtitle: phoneNumber ? `Connected with: ${phoneNumber}` : '',
        message: labels?.['modules-settings-whatsapp-busy-subtitle'] ?? 'modules-settings-whatsapp-busy-subtitle',
        color: 'warning' as const, // yellow/orange
        showQR: false
      };
    } else {
      // No configuration, unknown status, or undefined status
      return {
        type: 'none' as const,
        title: labels?.['modules-settings-whatsapp-not-configured-title'] ?? 'modules-settings-whatsapp-not-configured-title',
        subtitle: labels?.['modules-settings-whatsapp-not-configured-subtitle'] ?? 'modules-settings-whatsapp-not-configured-subtitle',
        message: labels?.['modules-settings-whatsapp-please-contact-your-administrator-to-initiate-the-whatsapp-bot'] ?? 'modules-settings-whatsapp-please-contact-your-administrator-to-initiate-the-whatsapp-bot',
        color: "#FFA726",
        showQR: false
      };
    }
  };

  const displayContent = getDisplayContent();

  return (
    <Box pl={{ xs: 1.25, sm: 2.5 }} pt={1} width="100%">
      {loading && (
        <Backdrop sx={{ color: theme.palette.secondary.main, zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
          <CircularProgress color="inherit" />
        </Backdrop>
      )}

      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        {channelName} Integration
      </Typography>

      {/* Stack Panels: Panel 2 first (QR/status), then Panel 1 (config) */}
      <Box display="flex" flexDirection={{ xs: 'column' }} gap={3} sx={{ maxWidth: 1200, margin: 'auto' }}>
        {/* Panel 2: QR/Status on Row 1 */}
        <Box sx={{ width: '100%' }}>
          <Card sx={{ p: 3, borderRadius: '12px', boxShadow: 3, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', flexGrow: 1 }}>
              <Typography variant="h3" color="secondary" gutterBottom sx={panelTitleSx}>
                {displayContent.title}
              </Typography>
              <Typography variant="h5" gutterBottom>
                {displayContent.subtitle}
              </Typography>
              {displayContent.showQR ? (
                <>
                  <QRCodeDisplay qrCodeString={configuration?.qrCodeString || ''} isOutdated={isQRCodeOutdated} />
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                      {displayContent.message}
                    </Typography>
                    {configuration?.qrCodeTimestamp && (
                      <Typography variant="caption" color={isQRCodeOutdated ? "error.main" : "textSecondary"} sx={{ display: 'block', mb: 1 }}>
                        Last updated: {new Date(configuration.qrCodeTimestamp).toLocaleString()}
                        {isQRCodeOutdated && ' (OUTDATED)'}
                      </Typography>
                    )}
                    {deducedStatus === 'QRCODE' && (
                      <Typography variant="caption" color="info.main" sx={{ display: 'block', mb: 1 }}>
                        Current time: {currentTime.toLocaleString()}
                      </Typography>
                    )}
                    {deducedStatus === 'QRCODE' && countdown > 0 && (
                      <Typography variant="caption" color={countdown <= 3 ? "warning.main" : "success.main"} sx={{ display: 'block', mb: 1, fontWeight: countdown <= 3 ? 'bold' : 'normal' }}>
                        Next refresh in: {countdown} seconds
                      </Typography>
                    )}
                    <Button variant={isQRCodeOutdated ? "contained" : "outlined"} color={isQRCodeOutdated ? "error" : "secondary"} startIcon={<Refresh />} onClick={refreshData} disabled={loading}>
                      {isQRCodeOutdated ? 'Refresh QR Code' : 'Refresh'}
                    </Button>
                  </Box>
                </>
              ) : displayContent.type === 'loading' ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <CircularProgress size={64} color="info" sx={{ mb: 2 }} />
                  <Typography variant="body1" color="info.main" sx={{ mb: 1 }}>
                    {displayContent.message}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Loading configuration...
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <QrCode2 sx={{ fontSize: 64, color: `${displayContent.color}.main`, mb: 2 }} />
                  <Typography variant="body1" color={`${displayContent.color}.main`} sx={{ mb: 1 }}>
                    {displayContent.message}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Status: {deducedStatus}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Panel 1: Configuration Information on Row 2 */}
        <Box sx={{ width: '100%' }}>
          <Card sx={{ p: 3, borderRadius: '12px', boxShadow: 3, height: '100%' }}>
            <CardContent>
              <Box 
                sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: { xs: 'center', sm: 'space-between' },
                  alignItems: { xs: 'center', sm: 'center' },
                  gap: { xs: 2, sm: 0 },
                  mb: 2 
                }}
              >
                <Typography
                  variant="h3"
                  color="secondary"
                  gutterBottom
                  sx={{ ...panelTitleSx, mb: { xs: 0, sm: 1 }, textAlign: { xs: 'center', sm: 'left' } }}
                >
                  WhatsApp Configuration
                </Typography>
              </Box>

              <Divider sx={{ my: 3 }} />

              {configuration ? (
                <Box display="flex" flexWrap="wrap" gap={3}>
                  <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)' } }}>
                    <TextField
                      label="Connected Phone Number"
                      value={configuration.phoneNumber || 'Not Connected'}
                      fullWidth
                      margin="normal"
                      InputProps={{ readOnly: true }}
                      variant="outlined"
                      size="small"
                      color={configuration.phoneNumber ? 'success' : 'error'}
                    />
                  </Box>
                  <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)' } }}>
                    <TextField
                      label="Status"
                      value={deducedStatus}
                      fullWidth
                      margin="normal"
                      InputProps={{ readOnly: true }}
                      variant="outlined"
                      size="small"
                      color={
                        deducedStatus === 'QRCODE' ? 'success' : 
                        deducedStatus === 'OFFLINE' ? 'error' : 
                        deducedStatus === 'ONLINE' ? 'success' : 
                        deducedStatus === 'BUSY' ? 'warning' : 
                        'error'
                      }
                    />
                  </Box>
                  {configuration.lastUpdated && (
                    <Box sx={{ width: '100%' }}>
                      <TextField
                        label="Last Updated"
                        value={new Date(configuration.lastUpdated).toLocaleString()}
                        fullWidth
                        margin="normal"
                        InputProps={{ readOnly: true }}
                        variant="outlined"
                        size="small"
                      />
                    </Box>
                  )}
                </Box>
              ) : (
                <Typography variant="body1" color="textSecondary" sx={{ textAlign: 'center', py: 5 }}>
                  No {channelName} configuration found. {canEdit ? 'Click "Add Configuration" to get started.' : 'Contact your administrator to configure WhatsApp.'}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* duplicate QR/status panel removed */}
      </Box>

      {/* Configuration Dialog removed (read-only) */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// Configuration Form Component
interface ConfigurationFormProps {
  configuration: any;
  onSave: (config: any) => Promise<void>;
  onCancel: () => void;
}

const ConfigurationForm: React.FC<ConfigurationFormProps> = ({ configuration, onSave, onCancel: _onCancel }) => {
  const [formData, setFormData] = useState({
    accessToken: configuration?.accessToken || '',
    phoneNumberId: configuration?.phoneNumberId || '',
    recipientNumber: configuration?.recipientNumber || '',
    testMessage: configuration?.testMessage || '',
    isActive: configuration?.isActive || false,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave(formData);
  };

  return (
    <Box component="form" id="configuration-form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      <TextField
        label="Access Token"
        value={formData.accessToken}
        onChange={(e) => setFormData({ ...formData, accessToken: e.target.value })}
        fullWidth
        margin="normal"
        required
        variant="outlined"
      />
      <TextField
        label="Phone Number ID"
        value={formData.phoneNumberId}
        onChange={(e) => setFormData({ ...formData, phoneNumberId: e.target.value })}
        fullWidth
        margin="normal"
        required
        variant="outlined"
      />
      <TextField
        label="Recipient Number (for testing)"
        value={formData.recipientNumber}
        onChange={(e) => setFormData({ ...formData, recipientNumber: e.target.value })}
        fullWidth
        margin="normal"
        required
        variant="outlined"
      />
      <TextField
        label="Test Message"
        value={formData.testMessage}
        onChange={(e) => setFormData({ ...formData, testMessage: e.target.value })}
        fullWidth
        margin="normal"
        required
        variant="outlined"
        multiline
        rows={3}
      />
    </Box>
  );
};
